import React, { useCallback, useEffect, useState } from 'react';
import { X, ClipboardList, MapPin, User, Phone, Building2, CalendarClock, CreditCard, Truck, Hash, Trash2, AlertTriangle, ChevronDown, ChevronUp, History } from 'lucide-react';
import { authed } from '../lib/api';
import { formatMoney } from '../lib/utils';
import { OrderStatusBadge, PaymentStatusBadge, normalizePaymentStatus } from './StatusBadges';

/**
 * OrderDetailsModal — Batch B2 Item 12 + Bug-Fix Pass 5 (delete flow)
 *
 * Rich order-details popup used by both the Super Admin dashboard (with
 * status edit + DELETE controls) and the Employee Admin "Check Orders" page
 * (view-only, NO delete UI, no knowledge of the feature).
 *
 * Props:
 *  - orderId: string (required)   — id of the order to load
 *  - realm: 'admin' | 'super_admin' — auth realm to use for API calls
 *  - canEdit: boolean               — true only for Super Admin
 *  - onClose: () => void
 *  - onStatusChanged: (order) => void  — parent should refresh its list
 *  - onDeleted: (orderId) => void      — Super Admin only, after successful delete
 */
export const OrderDetailsModal = ({ orderId, realm, canEdit, onClose, onStatusChanged, onDeleted }) => {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    // Delete flow (Super Admin only, two-step confirm + TOTP)
    const [deleteStep, setDeleteStep] = useState(0); // 0=idle, 1=confirm, 2=totp
    const [totpCode, setTotpCode] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [deleteErr, setDeleteErr] = useState('');
    // Employee-Admin state-machine flow: styled cancel confirmation modal.
    const [cancelConfirm, setCancelConfirm] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setErr('');
        try {
            const r = await authed(realm, { method: 'GET', url: `/admin/orders/${orderId}` });
            setOrder(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Failed to load order');
        } finally { setLoading(false); }
    }, [orderId, realm]);

    useEffect(() => { load(); }, [load]);

    const updateStatus = async (nextStatus) => {
        if (!canEdit || !order || nextStatus === order.status) return;
        setSaving(true); setErr('');
        try {
            const r = await authed(realm, { method: 'PUT', url: `/admin/orders/${order.id}/status`, data: { status: nextStatus } });
            setOrder({ ...r.data, distributor: order.distributor });
            onStatusChanged?.(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not update status');
        } finally { setSaving(false); }
    };

    // Independent from updateStatus — Order Status and Payment Status are
    // strictly decoupled; changing one must never touch the other.
    const [savingPayment, setSavingPayment] = useState(false);
    const [payErr, setPayErr] = useState('');
    const updatePaymentStatus = async (nextPayment) => {
        if (!canEdit || !order) return;
        const currentPay = normalizePaymentStatus(order.payment_status);
        if (nextPayment === currentPay) return;
        setSavingPayment(true); setPayErr('');
        try {
            const r = await authed(realm, {
                method: 'PUT',
                url: `/admin/orders/${order.id}/payment-status`,
                data: { payment_status: nextPayment },
            });
            setOrder({ ...r.data, distributor: order.distributor });
            onStatusChanged?.(r.data);
        } catch (e) {
            setPayErr(e?.response?.data?.detail || 'Could not update payment status');
        } finally { setSavingPayment(false); }
    };

    const confirmDelete = async () => {
        setDeleteErr('');
        if (!/^\d{6}$/.test(totpCode.trim())) { setDeleteErr('Enter the 6-digit Google Authenticator code.'); return; }
        setDeleting(true);
        try {
            await authed(realm, { method: 'POST', url: `/admin/orders/${order.id}/delete`, data: { totp_code: totpCode.trim() } });
            onDeleted?.(order.id);
            onClose?.();
        } catch (e) {
            setDeleteErr(e?.response?.data?.detail || 'Delete failed.');
        } finally { setDeleting(false); }
    };
    // Auth realm hard-check — this modal only exposes the delete flow when
    // canEdit is true AND realm is super_admin. Employee Admin should never
    // see the delete button under any circumstance.
    const canDelete = canEdit && realm === 'super_admin';

    // Business set (Batch B2). Legacy `packed` / `shipped` have been
    // migrated to `confirmed` / `dispatched` via the super-admin backfill.
    const STATUS_OPTIONS = ['placed', 'confirmed', 'dispatched', 'delivered', 'cancelled'];

    // Employee-Admin state machine — mirrors backend enforcement (which is
    // the true security boundary). Employee Admin must never move backward
    // and never see a cancel action after Dispatched.
    const isSuper = realm === 'super_admin';
    const isEmployee = canEdit && !isSuper;
    const NEXT_ACTIONS = {
        placed:     [{ status: 'confirmed',  label: 'Confirmed',  variant: 'primary' }, { status: 'cancelled', label: 'Cancelled', variant: 'danger' }],
        confirmed:  [{ status: 'dispatched', label: 'Dispatched', variant: 'primary' }, { status: 'cancelled', label: 'Cancelled', variant: 'danger' }],
        dispatched: [{ status: 'delivered',  label: 'Delivered',  variant: 'primary' }],
        delivered:  [],
        cancelled:  [],
    };
    const employeeActions = NEXT_ACTIONS[order?.status] || [];

    const handleEmployeeAction = (action) => {
        if (action.status === 'cancelled') { setCancelConfirm(true); return; }
        updateStatus(action.status);
    };
    const confirmEmployeeCancel = async () => {
        setCancelConfirm(false);
        await updateStatus('cancelled');
    };

    // Collapsible Payment Timeline (Super Admin only). Renders the
    // `payment_timeline` array captured server-side on every payment-status
    // change. Employee Admin never sees this section — the timeline is a
    // Super-Admin correction tool.
    const [showPaymentTimeline, setShowPaymentTimeline] = useState(false);
    const paymentTimeline = Array.isArray(order?.payment_timeline) ? order.payment_timeline : [];

    const dist = order?.distributor || {};
    const addr = order?.address || {};
    const items = order?.items || [];

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center px-3 py-6"
            role="dialog"
            aria-modal="true"
            data-testid="order-details-modal"
        >
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden bg-sdf-ivory rounded-2xl shadow-2xl border border-sdf-line flex flex-col animate-slide-up">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-sdf-line bg-white/70 backdrop-blur">
                    <div className="flex items-center gap-2">
                        <ClipboardList size={18} className="text-sdf-red" />
                        <div className="text-sm font-extrabold text-sdf-text tracking-widest uppercase">Order Details</div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full hover:bg-black/5"
                        aria-label="Close"
                        data-testid="order-details-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {loading ? (
                        <div className="py-12 text-center text-sdf-text-2 text-sm">Loading order…</div>
                    ) : err && !order ? (
                        <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2" data-testid="order-details-error">{err}</div>
                    ) : order ? (
                        <>
                            {/* Meta row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <MetaCard icon={<Hash size={14} />} label="Order ID" value={<span className="font-mono text-sm" data-testid="order-details-id">{order.order_number || order.id}</span>} />
                                <MetaCard icon={<CalendarClock size={14} />} label="Placed On" value={<span className="text-sm" data-testid="order-details-date">{new Date(order.created_at).toLocaleString()}</span>} />
                                <MetaCard icon={<Truck size={14} />} label="Status" value={
                                    <div className="flex flex-wrap items-center gap-2">
                                        <OrderStatusBadge status={order.status} testid="order-details-status-badge" />
                                        {isSuper && canEdit && (
                                            <select
                                                value={STATUS_OPTIONS.includes(order.status) ? order.status : 'placed'}
                                                onChange={(e) => updateStatus(e.target.value)}
                                                disabled={saving}
                                                className="border border-sdf-line rounded-md px-2 py-1 text-xs bg-white disabled:opacity-60"
                                                data-testid="order-details-status-select"
                                            >
                                                {STATUS_OPTIONS.map(s => (
                                                    <option key={s} value={s}>{s.toUpperCase()}</option>
                                                ))}
                                            </select>
                                        )}
                                        {isEmployee && employeeActions.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5" data-testid="employee-status-actions">
                                                {employeeActions.map(a => (
                                                    <button
                                                        key={a.status}
                                                        onClick={() => handleEmployeeAction(a)}
                                                        disabled={saving}
                                                        className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border disabled:opacity-60 transition-colors ${
                                                            a.variant === 'danger'
                                                                ? 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                                                                : 'bg-sdf-red text-white border-sdf-red hover:bg-red-700'
                                                        }`}
                                                        data-testid={`employee-status-btn-${a.status}`}
                                                    >
                                                        {a.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                } />
                                {(() => {
                                    // Payment Method (unchanged, always visible) + new two-state
                                    // Payment Status control. Q4 Option A: control sits inline with
                                    // the payment details, not with the order-status actions.
                                    const payKey = normalizePaymentStatus(order.payment_status);
                                    return (
                                        <MetaCard icon={<CreditCard size={14} />} label="Payment" value={
                                            <div className="text-sm space-y-2">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="font-bold" data-testid="order-details-payment-method">
                                                        {(order.payment_method || '').toUpperCase() || '—'}
                                                    </span>
                                                    <span className="text-sdf-text-2">·</span>
                                                    <PaymentStatusBadge status={payKey} testid="order-details-payment-badge" />
                                                </div>
                                                {canEdit && isSuper && (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <select
                                                            value={payKey}
                                                            onChange={(e) => updatePaymentStatus(e.target.value)}
                                                            disabled={savingPayment}
                                                            className="border border-sdf-line rounded-md px-2 py-1 text-xs bg-white disabled:opacity-60"
                                                            data-testid="order-details-payment-select"
                                                        >
                                                            <option value="pending">PENDING</option>
                                                            <option value="paid">PAID</option>
                                                        </select>
                                                        {savingPayment && <span className="text-[10px] text-sdf-text-2">Saving…</span>}
                                                    </div>
                                                )}
                                                {canEdit && isEmployee && payKey === 'pending' && (
                                                    <button
                                                        onClick={() => updatePaymentStatus('paid')}
                                                        disabled={savingPayment}
                                                        className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                                                        data-testid="employee-payment-btn-paid"
                                                    >
                                                        {savingPayment ? 'Saving…' : 'Paid'}
                                                    </button>
                                                )}
                                                {payErr && (
                                                    <div className="text-[11px] text-red-700 rounded bg-red-50 border border-red-200 px-2 py-1" data-testid="order-details-payment-error">
                                                        {payErr}
                                                    </div>
                                                )}
                                            </div>
                                        } />
                                    );
                                })()}
                            </div>

                            {/* Distributor + Delivery */}
                            <div className="card-soft p-4">
                                <div className="text-sdf-red font-bold text-[10px] tracking-widest mb-2">DISTRIBUTOR & DELIVERY</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                    <Field icon={<Building2 size={14} />} label="Business Name" value={dist.business_name || dist.person_name || addr.full_name || '—'} testid="od-business" />
                                    <Field icon={<User size={14} />} label="Contact Person" value={addr.full_name || dist.person_name || '—'} testid="od-person" />
                                    <Field icon={<Phone size={14} />} label="Phone" value={addr.phone || dist.mobile || order.distributor_mobile || '—'} testid="od-phone" />
                                    <Field icon={<MapPin size={14} />} label="City" value={addr.city || dist.city || '—'} testid="od-city" />
                                    <Field label="Address Line 1" value={addr.line1 || dist.address_line || '—'} testid="od-line1" full />
                                    {addr.line2 && <Field label="Address Line 2" value={addr.line2} testid="od-line2" full />}
                                    <Field label="State" value={addr.state || dist.state || '—'} testid="od-state" />
                                    <Field label="PIN Code" value={addr.pin_code || dist.pin_code || '—'} testid="od-pin" />
                                </div>
                            </div>

                            {/* Items */}
                            <div className="card-soft p-0 overflow-hidden">
                                <div className="px-4 py-3 border-b border-sdf-line bg-white/60 flex items-center justify-between">
                                    <div className="text-sdf-red font-bold text-[10px] tracking-widest">ORDERED PRODUCTS</div>
                                    <div className="text-[10px] text-sdf-text-2 uppercase tracking-widest">{items.length} item{items.length === 1 ? '' : 's'}</div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-sdf-cream/40 text-[10px] font-bold uppercase tracking-widest text-sdf-text-2">
                                            <tr>
                                                <th className="px-3 py-2 text-left">Product / Flavour</th>
                                                <th className="px-3 py-2 text-left">Variant</th>
                                                <th className="px-3 py-2 text-right">Qty</th>
                                                <th className="px-3 py-2 text-right">Price</th>
                                                <th className="px-3 py-2 text-right">Line Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((it, idx) => (
                                                <tr key={`${it.product_id}-${it.variant_id}-${it.flavour_id}-${idx}`} className="border-t border-sdf-line" data-testid={`od-item-${idx}`}>
                                                    <td className="px-3 py-2">
                                                        <div className="font-bold text-sdf-text line-clamp-1">{it.flavour_name || it.product_name || '—'}</div>
                                                        {it.product_name && it.flavour_name && it.product_name !== it.flavour_name && (
                                                            <div className="text-[10px] text-sdf-text-2">{it.product_name}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-xs">{it.size_label || '—'}</td>
                                                    <td className="px-3 py-2 text-right font-bold">{it.quantity}</td>
                                                    <td className="px-3 py-2 text-right">{formatMoney(it.price)}</td>
                                                    <td className="px-3 py-2 text-right font-extrabold">{formatMoney((it.price || 0) * (it.quantity || 0))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="border-t-2 border-sdf-line bg-sdf-cream/30">
                                                <td colSpan={4} className="px-3 py-3 text-right font-bold uppercase tracking-widest text-xs">Grand Total</td>
                                                <td className="px-3 py-3 text-right font-extrabold text-lg text-sdf-red" data-testid="od-grand-total">{formatMoney(order.total)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>

                            {order.notes && (
                                <div className="card-soft p-3">
                                    <div className="text-sdf-red font-bold text-[10px] tracking-widest mb-1">ORDER NOTES</div>
                                    <div className="text-sm text-sdf-text">{order.notes}</div>
                                </div>
                            )}

                            {order.placed_by && (
                                <div className="text-[11px] text-sdf-text-2 italic" data-testid="od-placed-by">
                                    Placed by {order.placed_by.actor_role === 'super_admin' ? 'Super Admin' : 'Admin'} · {order.placed_by.actor_email || '—'}
                                </div>
                            )}

                            {isSuper && paymentTimeline.length > 0 && (
                                <div className="card-soft p-0 overflow-hidden" data-testid="payment-timeline-card">
                                    <button
                                        type="button"
                                        onClick={() => setShowPaymentTimeline(v => !v)}
                                        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-sdf-cream/40 transition-colors"
                                        data-testid="payment-timeline-toggle"
                                        aria-expanded={showPaymentTimeline}
                                    >
                                        <div className="flex items-center gap-2">
                                            <History size={14} className="text-sdf-red" />
                                            <span className="text-sdf-red font-bold text-[10px] tracking-widest uppercase">Payment History</span>
                                            <span className="text-[10px] text-sdf-text-2">({paymentTimeline.length} {paymentTimeline.length === 1 ? 'entry' : 'entries'})</span>
                                        </div>
                                        {showPaymentTimeline ? <ChevronUp size={14} className="text-sdf-text-2" /> : <ChevronDown size={14} className="text-sdf-text-2" />}
                                    </button>
                                    {showPaymentTimeline && (
                                        <ul className="divide-y divide-sdf-line border-t border-sdf-line" data-testid="payment-timeline-list">
                                            {paymentTimeline.map((e, idx) => {
                                                // First entry is the initial `pending` seeded at order creation
                                                // (no `from` field) — render it as "Initial · pending".
                                                const isInit = !e.from;
                                                const from = isInit ? null : normalizePaymentStatus(e.from);
                                                const to = isInit ? normalizePaymentStatus(e.payment_status || 'pending') : normalizePaymentStatus(e.to);
                                                const roleLabel = e.by_role === 'super_admin' ? 'Super Admin'
                                                    : e.by_role === 'admin' ? 'Employee Admin'
                                                    : e.by_role === 'distributor' ? 'Distributor (auto)'
                                                    : (e.by_role || '—');
                                                const when = e.at ? new Date(e.at).toLocaleString() : '—';
                                                return (
                                                    <li key={idx} className="px-4 py-2.5 flex flex-wrap items-center gap-2 text-xs" data-testid={`payment-timeline-entry-${idx}`}>
                                                        {isInit ? (
                                                            <>
                                                                <span className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2">Initial</span>
                                                                <span className="text-sdf-text-2">→</span>
                                                                <PaymentStatusBadge status={to} />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PaymentStatusBadge status={from} />
                                                                <span className="text-sdf-text-2">→</span>
                                                                <PaymentStatusBadge status={to} />
                                                            </>
                                                        )}
                                                        <span className="text-sdf-text-2">·</span>
                                                        <span className="font-bold text-sdf-text">{roleLabel}</span>
                                                        {e.by_email && (
                                                            <span className="text-sdf-text-2">({e.by_email})</span>
                                                        )}
                                                        <span className="ml-auto text-sdf-text-2 whitespace-nowrap">{when}</span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            )}

                            {err && (
                                <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2">{err}</div>
                            )}
                        </>
                    ) : null}
                </div>

                {/* Delete flow (Super Admin only). Two-step: confirm → TOTP.
                    Rendered inline just above the footer so admin doesn't have
                    to navigate away. Employee Admin realm NEVER reaches this
                    branch because canDelete is false in that case. */}
                {canDelete && order && (deleteStep === 1 || deleteStep === 2) && (
                    <div className="border-t border-sdf-line px-5 py-4 bg-red-50/60 space-y-3" data-testid="order-delete-panel">
                        {deleteStep === 1 && (
                            <>
                                <div className="flex items-start gap-2 text-red-900">
                                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                                    <div className="text-sm font-bold">Are you sure you want to permanently delete this order?</div>
                                </div>
                                <div className="text-[11px] text-sdf-text-2">This action cannot be undone. The next step will ask for your Google Authenticator code.</div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => { setDeleteStep(2); setDeleteErr(''); }}
                                        className="inline-flex items-center gap-1 rounded-full bg-red-700 text-white font-bold px-4 py-1.5 text-xs hover:bg-red-800"
                                        data-testid="order-delete-confirm-yes"
                                    >Yes</button>
                                    <button
                                        onClick={() => { setDeleteStep(0); setDeleteErr(''); }}
                                        className="btn-outline text-xs"
                                        data-testid="order-delete-confirm-no"
                                    >No</button>
                                </div>
                            </>
                        )}
                        {deleteStep === 2 && (
                            <>
                                <div className="text-sdf-red font-bold text-[10px] tracking-widest">GOOGLE AUTHENTICATOR REQUIRED</div>
                                <div className="text-[11px] text-sdf-text-2">Enter the 6-digit code from your Google Authenticator app to permanently delete <b className="font-mono text-sdf-text">{order.order_number}</b>.</div>
                                <input
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    maxLength={6}
                                    inputMode="numeric"
                                    autoFocus
                                    className="w-full sm:w-52 border border-sdf-line rounded-xl px-3 py-2.5 outline-none text-sm bg-white tracking-widest font-bold"
                                    placeholder="123456"
                                    data-testid="order-delete-totp"
                                />
                                {deleteErr && (
                                    <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2" data-testid="order-delete-error">{deleteErr}</div>
                                )}
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={confirmDelete}
                                        disabled={deleting || totpCode.length !== 6}
                                        className="inline-flex items-center gap-1 rounded-full bg-red-700 text-white font-bold px-4 py-1.5 text-xs hover:bg-red-800 disabled:opacity-60"
                                        data-testid="order-delete-confirm"
                                    >{deleting ? 'Deleting…' : 'Permanently Delete'}</button>
                                    <button
                                        onClick={() => { setDeleteStep(0); setTotpCode(''); setDeleteErr(''); }}
                                        className="btn-outline text-xs"
                                        data-testid="order-delete-cancel"
                                    >Cancel</button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="border-t border-sdf-line px-5 py-3 flex items-center justify-between gap-2 bg-white/60">
                    <div>
                        {canDelete && deleteStep === 0 && (
                            <button
                                onClick={() => setDeleteStep(1)}
                                className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-50"
                                data-testid="order-delete-open"
                                title="Permanently delete this order"
                            >
                                <Trash2 size={12} /> Delete Order
                            </button>
                        )}
                    </div>
                    <button onClick={onClose} className="btn-outline" data-testid="order-details-footer-close">Close</button>
                </div>
            </div>
            {/* Employee-Admin cancel confirmation modal — styled, matches app patterns. */}
            {isEmployee && cancelConfirm && (
                <div
                    className="absolute inset-0 z-[70] flex items-center justify-center px-4"
                    role="dialog"
                    aria-modal="true"
                    data-testid="employee-cancel-confirm"
                >
                    <div className="absolute inset-0 bg-black/60" onClick={() => setCancelConfirm(false)} />
                    <div className="relative w-full max-w-sm bg-sdf-ivory rounded-2xl shadow-popup overflow-hidden border border-sdf-line">
                        <div className="px-4 py-3 border-b border-sdf-line">
                            <div className="text-sm font-extrabold text-red-700 tracking-widest">CANCEL ORDER</div>
                        </div>
                        <div className="p-4 space-y-2">
                            <p className="text-sm text-sdf-text">Are you sure you want to cancel this order?</p>
                            <p className="text-xs text-sdf-text-2">
                                <span className="font-mono">{order?.order_number}</span>
                                {order?.total ? ` · ${formatMoney(order.total)}` : ''}
                            </p>
                            <p className="text-[11px] text-sdf-text-2">This action cannot be undone by you. Please confirm before proceeding.</p>
                        </div>
                        <div className="border-t border-sdf-line px-4 py-3 flex items-center justify-end gap-2">
                            <button onClick={() => setCancelConfirm(false)} disabled={saving} className="btn-outline" data-testid="employee-cancel-no">No</button>
                            <button
                                onClick={confirmEmployeeCancel}
                                disabled={saving}
                                className="inline-flex items-center gap-1.5 rounded-full bg-red-700 text-white font-bold px-4 py-2 text-sm hover:bg-red-800 disabled:opacity-60"
                                data-testid="employee-cancel-yes"
                            >
                                {saving ? 'Cancelling…' : 'Yes, Cancel Order'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const MetaCard = ({ icon, label, value }) => (
    <div className="card-soft p-3">
        <div className="flex items-center gap-1.5 text-sdf-text-2 text-[10px] font-bold tracking-widest uppercase mb-1">
            {icon}
            <span>{label}</span>
        </div>
        <div>{value}</div>
    </div>
);

const Field = ({ icon, label, value, testid, full }) => (
    <div className={full ? 'md:col-span-2' : ''}>
        <div className="text-[10px] font-bold text-sdf-text-2 tracking-widest uppercase mb-0.5 flex items-center gap-1">
            {icon} <span>{label}</span>
        </div>
        <div className="text-sm text-sdf-text font-medium break-words" data-testid={testid}>{value}</div>
    </div>
);

const STATUS_COLORS = {
    // Legacy local map — retained for any lingering imports; primary source
    // of truth is `./StatusBadges` (OrderStatusBadge). Kept for backward
    // compatibility only; new code MUST use OrderStatusBadge.
    placed: 'bg-neutral-100 text-neutral-700 border-neutral-300',
    confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
    dispatched: 'bg-orange-100 text-orange-800 border-orange-300',
    packed: 'bg-orange-100 text-orange-800 border-orange-300',
    shipped: 'bg-orange-100 text-orange-800 border-orange-300',
    delivered: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300',
};

// Legacy pill kept only for external consumers. New code uses OrderStatusBadge.
const StatusPill = ({ status }) => {  // eslint-disable-line no-unused-vars
    const cls = STATUS_COLORS[status] || 'bg-neutral-100 text-neutral-800 border-neutral-200';
    return (
        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border ${cls}`} data-testid="order-status-pill">
            {status || 'unknown'}
        </span>
    );
};

export default OrderDetailsModal;
